import { getMonthFromDate } from "../extensions/date";
import { titleCase } from "../extensions/extension";
import { GridcoDiscomData, IntrastateGen, GridoIsgs } from "../models/gridco";
import {
  RajasthanDiscomDrawal,
  RajasthanDiscomData,
  RajasthanGenData,
  RajasthanIsgs,
  RajasthanCollective,
  RajasthanGnaTgna,
  ReEnergyData,
  RajProcurementRecord,
  RajGenParams,
  RajasthanUploadFile,
  RajGenProfile,
  RajProcurement,
  RajTamData,
  RajBankingData,
} from "../models/rajasthan";
import {
  buildHttpResponse,
  HttpMethod,
  buildMultipartHttpResponse,
} from "./Network_utility";
import { ApiResponse } from "../models/api_res";
/**
 * Recursively traverses an object/array and adds the given randomValue
 * to every numeric field that is greater than 100.
 */
function addRandomToNumberFields(obj: any, randomValue: number): any {
  if (typeof obj === "number") {
    return obj > 100 ? obj + randomValue : obj;
  } else if (Array.isArray(obj)) {
    return obj.map((item) => addRandomToNumberFields(item, randomValue));
  } else if (typeof obj === "object" && obj !== null) {
    const newObj: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = addRandomToNumberFields(obj[key], randomValue);
      }
    }
    return newObj;
  }
  return obj;
}

/**
 * Helper to generate a random integer between 100 and 150 (inclusive).
 */
function generateRandomValue(): number {
  return Math.floor(Math.random() * (150 - 100 + 1)) + 100;
}

export async function getRajasthanDiscomDrawlData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any[] = await buildHttpResponse({
      endPoint: `data/getRajasthanDiscomDrawal?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    console.log("====================================");
    console.log(response);
    const mapped = response.map((item) => {
      return {
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        area: item[3],
        mw: item[4],
        lu: item[4] / 400,
        discom: item[5],
        mwh: 0,
      };
    });

    const discoms: string[] = [];
    const area: string[] = [];
    for (let i = 0; i < mapped.length; i++) {
      const element = mapped[i].discom;
      const element2 = mapped[i].area;
      if (discoms.includes(element)) {
        continue;
      } else {
        discoms.push(element);
      }
      if (area.includes(element2)) {
        continue;
      } else {
        area.push(element2);
      }
    }

    const modifiedMapping = mapped.map((item) => {
      const discomIndex = discoms.indexOf(item.discom);
      const newDiscomName = `Discom${discomIndex + 1}`;
      const areaIndex = area.indexOf(item.area);
      const newAreaName = `Area${areaIndex + 1}`;
      return {
        ...item,
        discom: newDiscomName,
        area: newAreaName,
      };
    });

    return addRandomToNumberFields(modifiedMapping, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanAreaForecastData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanAreaForecastData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    console.log("====================================");
    console.log(response);
    const mapped = response.map((item) => {
      return {
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        area: item[3],
        mw: item[4],
        discom: item[5],
        mwh: 0,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanDiscomData(
  startDate: string,
  endDate: string
): Promise<RajasthanDiscomData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: Promise<RajasthanDiscomData[]> = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const res = await response;
    const uniqueDates = Array.from(new Set(res.map((item) => item.date)));

    const discoms: string[] = [];
    for (let i = 0; i < res.length; i++) {
      const element = res[i].discom;
      if (discoms.includes(element)) {
        continue;
      } else {
        discoms.push(element);
      }
    }

    const mapped = res.map((item) => {
      const discomIndex = discoms.indexOf(item.discom);

      const newDiscomName = `Discom${discomIndex + 1}`;

      return {
        ...item,
        discom: newDiscomName,
        drawal_mwh: item.drawal_mw / 4,
        drawal_mw: item.drawal_mw,
        drawal_lu: item.drawal_mw / 400,
        total_mwh: (item.load_shedding_mw + item.drawal_mw) / 4,
        total_mw: item.load_shedding_mw + item.drawal_mw,
        schedule_mw: item.schedule_mw,
        schedule_mwh: item.schedule_mw / 8,
        schedule_lu: item.schedule_mw / 400,
        load_shedding_mwh: item.load_shedding_mw / 4,
        load_shedding_lu: item.load_shedding_mw / 400,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
      };
    });

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanForecastDiscomData(
  startDate: string,
  endDate: string,
  model: string
): Promise<RajasthanDiscomData[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getDiscomForecast?start_date=${startDate}&end_date=${endDate}&model=${model}`,
      method: HttpMethod.GET,
    });
    const res = await response;
    const uniqueDates = Array.from(
      new Set(res.map((item: RajasthanDiscomData) => item.date))
    );
    const discoms: string[] = [];
    for (let i = 0; i < res.length; i++) {
      const element = res[i].discom;
      if (discoms.includes(element)) {
        continue;
      } else {
        discoms.push(element);
      }
    }
    const mapped = res.map((item: RajasthanDiscomData) => {
      const discomIndex = discoms.indexOf(item.discom);

      const newDiscomName = `Discom${discomIndex + 1}`;
      return {
        ...item,
        discom: newDiscomName,
        drawal_mwh: (item.drawal_mw + 0) / 4,
        drawal_mw: item.drawal_mw + 0,
        drawal_lu: (item.drawal_mw + 0) / 400,
        total_mwh: item.drawal_mw / 4,
        total_mw: item.drawal_mw,
        total_lu: item.drawal_mw / 400,
        schedule_mwh: item.schedule_mw / 4,
        schedule_mw: item.schedule_mw,
        schedule_lu: item.schedule_mw / 400,
        load_shedding_mwh: 0,
        load_shedding_lu: 0,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

function applyBlockReplacements(
  data: RajasthanDiscomData[]
): RajasthanDiscomData[] {
  const replacementMappings: {
    targetDate: string;
    discom: string;
    blocks: number[];
    sourceDate: string;
  }[] = [
    {
      targetDate: "2024-12-10",
      discom: "Jodhpur",
      blocks: [82],
      sourceDate: "2024-12-09",
    },
    {
      targetDate: "2024-12-10",
      discom: "Ajmer",
      blocks: [82],
      sourceDate: "2024-12-09",
    },
  ];

  const modifiedData = [...data]; // Create a copy to avoid modifying original data

  replacementMappings.forEach(({ targetDate, discom, blocks, sourceDate }) => {
    blocks.forEach((block) => {
      const sourceRecord = data.find(
        (record) =>
          record.date === sourceDate &&
          record.block === block &&
          record.discom === discom
      );

      if (sourceRecord) {
        modifiedData.forEach((record) => {
          if (
            record.date === targetDate &&
            record.block === block &&
            record.discom === discom
          ) {
            record.schedule_mw = sourceRecord.schedule_mw; // Apply the replacement
          }
        });
      }
    });
  });

  return modifiedData;
}

function convertToRajasthanGenData(params: any[]): RajasthanGenData {
  return {
    date: params[0],
    month: getMonthFromDate(params[0]),
    state: "State 1",
    source: params[6],
    slot: params[1],
    station: params[2],
    onBarDS_mw: params[3],
    value_mw: params[3],
    value_mwh: params[3] / 4,
    onBarDc_mw: params[4],
    backdown_mw: params[5],
    onBarDS_mwh: params[3] / 4,
    onBarDc_mwh: params[4],
    backdown_mwh: params[5] / 4,
  };
}

export async function getRajasthanGenerationData(
  startDate: string,
  endDate: string
): Promise<RajasthanGenData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any[][] = await buildHttpResponse({
      endPoint: `rajasthan/getGenerationData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const mapped = response.map((item) => {
      return convertToRajasthanGenData(item);
    });

    const station: string[] = [];
    for (let i = 0; i < mapped.length; i++) {
      const element = mapped[i].station;
      if (station.includes(element)) {
        continue;
      } else {
        station.push(element);
      }
    }

    const modifiedMapping = mapped.map((item) => {
      const stationIndex = station.indexOf(item.station);

      const newStationName = `Station${stationIndex + 1}`;

      return {
        ...item,
        station: newStationName,
      };
    });

    return addRandomToNumberFields(modifiedMapping, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Generation data:", error);
    return [];
  }
}

export async function getRajasthanIsgsData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanIsgs[]> {
  try {
    const randomValue = generateRandomValue();
    const response = (await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanIsgsData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    })) as RajasthanIsgs[];

    const state: string[] = [];
    const utility: string[] = [];
    for (let i = 0; i < response.length; i++) {
      const element = response[i].state;
      const element2 = response[i].from_utility__name;
      if (state.includes(element)) {
        continue;
      } else {
        state.push(element);
      }
      if (utility.includes(element2)) {
        continue;
      } else {
        utility.push(element2);
      }
    }
    const mapped = response.map((item) => {
      const stateIndex = state.indexOf(item.state);
      const newStateName = `State${stateIndex + 1}`;
      const utilityIndex = state.indexOf(item.from_utility__name);
      const newUtilityName = `Utility${utilityIndex + 1}`;
      return {
        ...item,
        value_mwh: item.value_mw / 4,
        station: item.from_utility__name,
        month: getMonthFromDate(item.date),
        state: newStateName,
        from_utility__name: newUtilityName,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan ISGS Data:", error);
    return [];
  }
}

export async function getRajasthanCollectiveData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanCollective[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getCollectiveData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Collective Data:", error);
    return [];
  }
}

export async function getGnaTgnaData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanGnaTgna[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getGnaTgnaData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // Define fictional entity names for utilities
    const utilityNames = [
      "PowerGen Alpha",
      "EnergyX",
      "VoltaPower",
      "GridStar",
      "NeoEnergy",
      "PowerNorth",
      "SouthGrid",
      "EastPower",
      "WesternElectric",
      "CentralGen",
    ];

    // Define fictional state names
    const stateNames = [
      "StateA",
      "StateB",
      "StateC",
      "NorthState",
      "SouthState",
      "EastRegion",
      "WestRegion",
      "CentralZone",
    ];

    // Track which real name maps to which fictional name
    const fromUtilityMap = new Map<string, string>();
    const toUtilityMap = new Map<string, string>();
    const fromStateMap = new Map<string, string>();
    const toStateMap = new Map<string, string>();
    const entityStateMap = new Map<string, string>(); // Add map for entity states

    let fromUtilityCounter = 0;
    let toUtilityCounter = 0;
    let fromStateCounter = 0;
    let toStateCounter = 0;
    let entityStateCounter = 0; // Add counter for entity states

    const mapped: RajasthanGnaTgna[] = response.map(
      (item: RajasthanGnaTgna) => {
        // Get or create mapping for from_utility__name
        let fromUtilityName: string;
        if (fromUtilityMap.has(item.from_utility__name)) {
          fromUtilityName = fromUtilityMap.get(item.from_utility__name)!;
        } else {
          fromUtilityName =
            utilityNames[fromUtilityCounter % utilityNames.length];
          fromUtilityMap.set(item.from_utility__name, fromUtilityName);
          fromUtilityCounter++;
        }

        // Get or create mapping for to_utility__name
        let toUtilityName: string;
        if (toUtilityMap.has(item.to_utility__name)) {
          toUtilityName = toUtilityMap.get(item.to_utility__name)!;
        } else {
          toUtilityName = "Consumer" + (toUtilityCounter + 1);
          toUtilityMap.set(item.to_utility__name, toUtilityName);
          toUtilityCounter++;
        }

        // Handle from_state__name if it exists
        let fromStateName: string | null = null;
        if (item.from_state__name) {
          if (fromStateMap.has(item.from_state__name)) {
            fromStateName = fromStateMap.get(item.from_state__name)!;
          } else {
            fromStateName = stateNames[fromStateCounter % stateNames.length];
            fromStateMap.set(item.from_state__name, fromStateName);
            fromStateCounter++;
          }
        }

        // Handle to_state__name
        let toStateName: string;
        if (toStateMap.has(item.to_state__name)) {
          toStateName = toStateMap.get(item.to_state__name)!;
        } else {
          toStateName = stateNames[toStateCounter % stateNames.length];
          toStateMap.set(item.to_state__name, toStateName);
          toStateCounter++;
        }

        // Handle from_utility__entity_state__name if it exists
        let entityStateName: string | null = null;
        if (item.from_utility__entity_state__name) {
          if (entityStateMap.has(item.from_utility__entity_state__name)) {
            entityStateName = entityStateMap.get(
              item.from_utility__entity_state__name
            )!;
          } else {
            entityStateName =
              stateNames[entityStateCounter % stateNames.length];
            entityStateMap.set(
              item.from_utility__entity_state__name,
              entityStateName
            );
            entityStateCounter++;
          }
        }

        // Map IR link name
        const linkName = `Link-${
          fromStateName?.charAt(0) || "X"
        }${toStateName.charAt(0)}`;

        return {
          ...item,
          from_utility__name: fromUtilityName,
          to_utility__name: toUtilityName,
          from_state__name: fromStateName,
          to_state__name: toStateName,
          from_utility__entity_state__name: entityStateName, // Add anonymized entity state
          ir_link__name: linkName,
        };
      }
    );

    console.log(mapped);
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return [];
  }
}

export async function createRajProcurementRecord(
  formData: any
): Promise<string> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/procurement/create`,
      method: HttpMethod.POST,
      body: formData,
    });
    return response.message;
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return "Inserting error";
  }
}

export async function getRajFileTypes(): Promise<string[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: "/rajasthan/get-file-types",
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj File Types:", error);
    return [];
  }
}

export async function uploadRajFile(formData: any): Promise<string> {
  const response = await buildMultipartHttpResponse({
    endPoint: "/rajasthan/upload-file", // Replace with your endpoint
    method: HttpMethod.POST,
    formData,
  });
  return response.message;
}

export async function getRajReData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<ReEnergyData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: ReEnergyData[] = await buildHttpResponse({
      endPoint: `/rajasthan/getRenewableGeneration?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const mapped = response.map((item) => {
      return {
        ...item,
        value_mwh: item.value_mw / 4,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Renewable Generation data:", error);
    return [];
  }
}

export async function getProcurementRecord(
  page: any,
  perpage: any
): Promise<RajProcurementRecord[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await fetch(
      `https://datahub.gna.energy/rajasthan_procurement?page=${page}&per_page=${perpage}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return addRandomToNumberFields(data.data, randomValue);
  } catch (error) {
    console.error("Error fetching Procurement Record :", error);
    return [];
  }
}

export async function getRajOperatingPar(): Promise<RajGenParams[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getRajOperatingPar`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Operating Parameters data:", error);
    return [];
  }
}

export async function getRajFiles(): Promise<RajasthanUploadFile[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getRajUploadedFile`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Files data:", error);
    return [];
  }
}

export async function rajChatGptRes({
  query,
  thread_token,
}: {
  query: string;
  thread_token: string;
}): Promise<{ sender: String; content: String }> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/handeUserQuery?query=${query}&thread_token=${thread_token}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function uploadGridcoFile(formData: any): Promise<string> {
  const response = await buildMultipartHttpResponse({
    endPoint: "/gridco/upload_gridco_file", // Replace with your endpoint
    method: HttpMethod.POST,
    formData,
  });
  return response.message;
}

export async function createFileType(name: string): Promise<string> {
  const response = await buildHttpResponse({
    endPoint: "/gridco/create_file_type", // Replace with your endpoint
    method: HttpMethod.POST,
    body: { name },
  });
  return response.message;
}

export async function deleteFileType(name: string): Promise<string> {
  const response = await buildHttpResponse({
    endPoint: "/gridco/delete_file_type", // Replace with your endpoint
    method: HttpMethod.POST,
    body: { name },
  });
  return response.message;
}

export async function getGridcoUploadedFile(): Promise<string[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: "/gridco/getGridcoUploadedFile",
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco Uploaded File:", error);
    return [];
  }
}

export async function getGridcoFileTypes(): Promise<string[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: "/gridco/get_gridco_file_types",
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco File Types:", error);
    return [];
  }
}

export async function getRajGenProfileData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajGenProfile[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getGenProfileData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Gen Profile Data:", error);
    return [];
  }
}

export async function getRajDeep({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getRajDeep?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Deep data:", error);
    return [];
  }
}

export async function getRajIntraGen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getRajIntraGen?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj IntraGen data:", error);
    return [];
  }
}

export async function getRajIsgs({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getRajIsgs?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Isgs data:", error);
    return [];
  }
}

export async function getGridcoIntraGen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<IntrastateGen[]> {
  try {
    const randomValue = generateRandomValue();
    const response: IntrastateGen[] = await buildHttpResponse({
      endPoint: `gridco/get_intrastate_gen_data?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const mapped = response.map((item) => {
      return {
        ...item,
        generator_type: titleCase(item.generator_type),
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco IntraGen data:", error);
    return [];
  }
}

export async function getRajTamData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajTamData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: RajTamData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajNoarData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Tam data:", error);
    return [];
  }
}

export async function getRajNoar({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajTamData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: RajTamData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajNoarData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Noar data:", error);
    return [];
  }
}

export async function getGridcoIsgs({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<GridoIsgs[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `gridco/get_grido_isgs_data?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco ISGS data:", error);
    return [];
  }
}

export async function getGridcoRegen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<GridoIsgs[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `gridco/get_re_gen_data?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco Regen data:", error);
    return [];
  }
}

export async function getRajBankingData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajBankingData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: RajBankingData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajBankingData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const mapped = response.map((item) => {
      return {
        ...item,
        mwh: item.mwh,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Banking data:", error);
    return [];
  }
}

// MOD Demo START POST Session
export async function fetchgetModSession(startdate: string) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/startSession?date=${startdate}`,
    method: HttpMethod.POST,
    body: {
      date: startdate,
    },
  });
  return response;
}

export async function getModDemoSession() {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSession`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModDemoArchive(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getPreviousSession?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD Stage data
export async function ModStagePost(stage: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postStage`,
    method: HttpMethod.POST,
    body: {
      stage: stage,
      session: session,
    },
  });
  return response;
}
export async function ModActivateSession(session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/activateSession`,
    method: HttpMethod.POST,
    body: {
      session: session,
    },
    noCache: true,
  });
  return response;
}

export async function fetchModDamDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDefaultData/dam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModGDamDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDefaultData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD RTM default data
export async function fetchModRtmDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDefaultData/rtm?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD Demand default data
export async function fetchModDemandDefault(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDemandDefault?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// upload file POST DAM RTM DEMAND
export async function ModDamData(ModDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postDam`,
    method: HttpMethod.POST,
    body: ModDamdata,
  });

  return response as ApiResponse;
}

export async function ModRtmData(ModRtmdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postRtm`,
    method: HttpMethod.POST,
    body: ModRtmdata,
  });

  return response as ApiResponse;
}
export async function ModDemandData(ModDemanddata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postDemand`,
    method: HttpMethod.POST,
    body: ModDemanddata,
  });

  return response as ApiResponse;
}
export async function ModAggregatedLoadData(
  ModAggregatedData: any
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postAggregatedLoadPrice`,
    method: HttpMethod.POST,
    body: ModAggregatedData,
  });

  return response as ApiResponse;
}

// MOD DAM fetch Data
export async function fetchModDamData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchModGDamData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModGDamData(ModGDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postGDam`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModGDamdata,
  });

  return response as ApiResponse;
}

export async function fetchModDemandData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDemand?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD RTM fetch Data
export async function fetchModRtmData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getRtm?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
// MOD TABLE fetch Data
export async function fetchModTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getMOD?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModTableChartData(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getMODChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// BID final TABLE fetch Data
export async function fetchModBidTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function ModDemoUpdateBidData(
  ModUpdatedata: any
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postBid`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}

export async function getModDemoBidSimulationData(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
// post uploadGridcoModFile

export async function ModBulkUploadFile(formData: any): Promise<{
  automationMod_files: Array<{ file_url: string }>;
}> {
  const response = await buildMultipartHttpResponse({
    endPoint: "mod_demo/automation",
    method: HttpMethod.POST,
    formData,
  });
  return response;
}

export async function ModUpdateData(ModUpdatedata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postMOD`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}

export async function CreateBidTable(ModUpdatedata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/createBid`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}
export async function fetchgetBidDownload(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getBidDownload?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}

//Simulator Summary
export async function fetchModSimulationSummary(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorGenChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

//Simulator Station
export async function fetchModSimulationStation(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getData4?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
  });
  return response;
}

//Simulator Cost of Power
export async function fetchModSimulationCostPower(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorCostPower?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
  });
  return response;
}
//Simulator Sale Revenue (IEX DAM)
export async function fetchModSimulationSaleRevenue(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorSalesRevenue?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function fetchModSimulationTopBuyer(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorGenPrice?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchgetTemplateIPP(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/downloadIPP?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}
export async function fetchgetTemplateISGS(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/downloadISGS?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}
export async function fetchgetTemplateSGU(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `/mod_demo/downloadSGU?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}
export async function fetchLgbrTableData(
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/get-lgbr?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchLgbrGetData(
  type: string | null,
  time_slot: number| null,
  startdate: string,
  session: number
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/get-lgbr-generator?date=${startdate}&session=${session}&type=${type}&time_slot=${time_slot}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
